/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useLocale } from "../../../i18n/hooks";
import { selectBrand, stringToFileName } from "../resourcesSlice";
import { ReactComponent as Caret } from "bootstrap-icons/icons/arrow-bar-left.svg";
import { ReactComponent as Filter } from "bootstrap-icons/icons/filter.svg";
import { ReactComponent as Cap } from "bootstrap-icons/icons/mortarboard.svg";
import ResourceCard from "../resourceCard/ResourceCard";
import { useTranslation } from "react-i18next";
import "./ResourcesIndexUI.scss";
import {
  QualificationId,
  ResourceCard as ResourceCardType,
  ResourceLink,
  ResourceReference,
  SpecItemData,
  UserResourceReference,
} from "../../../types";
import { useAppSelector } from "../../../app/hooks";
import { Button, Row, Spinner } from "react-bootstrap";
import SubjectIcon from "../../subject/SubjectIcon";
import FeatureFlag from "../../featureFlag/FeatureFlag";
import { useGetUserQuery } from "../../../app/services/userApi";
import { skipToken } from "@reduxjs/toolkit/query";
import Filters, { AvailableFilters } from "../resourceFilter/ResourcesFilter";
import { ErrorBoundary } from "../../errorBoundary/ErrorBoundary";
import { Subject } from "../../../app/services/subjectApi";
import ResourceSort from "../resourceSort/ResourceSort";
import { filterResources, getCurrentParams, updateQueryStorage } from "./utils";
import ResourceCurrentFilter from "../resourceCurrentFilters/ResourceCurrentFilters";
import PlaceholderCard from "./PlaceholderCard";
import { Status } from "../resourceFilter/statusFilter/StatusFilter";
import MyResourcesCount from "./MyResourcesCount";

type Props = {
  resources: ResourceReference[] | UserResourceReference[];
  subject?: Subject;
  filters: AvailableFilters[];
};

export type QueryProps = {
  types: string[];
  tags: string[];
  cats: string[];
  level: string | undefined;
  qualification: QualificationId | undefined;
  specItem: string | undefined;
  sort: string | undefined;
  status: Status | undefined;
};

const ResourcesIndexUI: React.FC<Props> = ({ resources, subject, filters }) => {
  const [filteredResources, setFilteredResources] = useState<
    UserResourceReference[] | ResourceReference[]
  >();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState<QueryProps>({
    cats: [],
    level: undefined,
    qualification: undefined,
    sort: undefined,
    specItem: undefined,
    tags: [],
    types: [],
    status: undefined,
  });

  const [specItems, setSpecItems] = useState<SpecItemData>();
  const [noSpecItems, setNoSpecItems] = useState<boolean>(); // flag to show if spec items are found or not

  const locale = useLocale();
  const { t: _ } = useTranslation();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const brand = useSelector(selectBrand());

  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;

  const { data: user } = useGetUserQuery(
    isAuthenticated ? undefined : skipToken,
  );

  useEffect(() => {
    if (resources && resources.length > 0) {
      setFilteredResources(
        filterResources(
          query,
          [...resources]?.filter((resource) =>
            (user && (user.role === "wjec" || user.role === "admin")) ||
            resource.PK.includes("USER:")
              ? resource
              : resource.Status === "live",
          ),
        ),
      );
    } else {
      setFilteredResources([]);
    }

    return () => {
      setFilteredResources([]);
    };
  }, [resources, query, locale, user]);

  // Load previous query if no query is set and PREV_QUERY exists in either local or session storage
  useEffect(() => {
    let localQuery;
    let sessionQuery;

    try {
      localQuery = JSON.parse(localStorage.getItem("PREV_QUERY") ?? "{}");
    } catch {
      localQuery = undefined;
    }

    try {
      sessionQuery = JSON.parse(sessionStorage.getItem("PREV_QUERY") ?? "{}");
    } catch {
      localQuery = undefined;
    }

    const key = subject ? subject.id : "my-resources";

    if (!(searchParams.toString().length > 0)) {
      const query = new URLSearchParams(
        (localQuery?.[key] ?? "") + "&" + (sessionQuery?.[key] ?? ""),
      ).toString();
      if (query) setSearchParams(query);
    }
  }, [subject, searchParams, setSearchParams]);

  // Set query data based on search
  useEffect(() => {
    setQuery({
      cats: searchParams.get("cats")?.split(",") || [],
      level: searchParams.get("level") || undefined,
      qualification:
        (searchParams.get("qualification") as QualificationId) || undefined,
      sort: searchParams.get("sort") || undefined,
      specItem: searchParams.get("spec") || undefined,
      tags: searchParams.get("tags")?.split("|") || [],
      types: searchParams.get("types")?.split(",") || [],
      status: (searchParams.get("status") as Status) || undefined,
    });
  }, [searchParams]);

  /** Function to handle adding query to search params */
  const handleQueryItemClick = (
    thisValue: string,
    thisQueryType: keyof QueryProps,
  ) => {
    // const params = getCurrentParams()
    // if (thisCat && thisCat !== cat) params.cat = thisCat
    // else if (params.cat) delete params.cat
    // setSearchParams(params)
    const params = getCurrentParams(query);
    const paramType = thisQueryType === "specItem" ? "spec" : thisQueryType;

    if (thisValue !== undefined) {
      if (Array.isArray(query[thisQueryType])) {
        const _cats = params[paramType]
          ? params[paramType].split(thisQueryType === "tags" ? "|" : ",")
          : [];
        if (_cats.includes(thisValue)) {
          _cats.splice(_cats.indexOf(thisValue), 1);
        } else {
          _cats.push(thisValue);
        }
        if (_cats.length > 0)
          params[paramType] = _cats.join(thisQueryType === "tags" ? "|" : ",");
        else if (params[paramType]) delete params[paramType];
      } else {
        if (thisValue && thisValue !== query[thisQueryType])
          params[paramType] = thisValue;
        else if (params[paramType]) delete params[paramType];
        if (thisQueryType === "level" && params.sort?.includes("level"))
          delete params.sort;
        if (thisQueryType === "qualification" && params.spec)
          delete params.spec;
      }
    }

    updateQueryStorage(params, subject ? subject.id : "my-resources");
    setSearchParams(params);
  };

  const handleClearClick = useCallback(() => {
    updateQueryStorage({}, subject ? subject.id : "my-resources");
    setSearchParams({});
    setNoSpecItems(false);
  }, [subject, setSearchParams]);

  const getSpecItems = useCallback(
    async (subjectId: string, qualification: string) => {
      let formattedQualification = qualification
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase();
      let formattedSubjectId = subjectId.toLowerCase();
      try {
        const data = require(
          `../../../json/specItems/${formattedQualification}-${formattedSubjectId}.json`,
        );
        setSpecItems(data);
        setNoSpecItems(false);
      } catch {
        setNoSpecItems(true);
      }
    },
    [],
  );

  useEffect(() => {
    const { qualification } = query;
    if (qualification && subject) {
      getSpecItems(subject.id, qualification);
    } else {
      setSpecItems(undefined);
      setNoSpecItems(false); // used to hide no spec item
    }
  }, [query, subject, getSpecItems]);

  const ErrorFallback = (resource: ResourceReference | UserResourceReference) => {
    return (
      <div className="alert alert-danger">
        <p>{`${_("An error has occurred while loading this resource.")}`}</p>
        <code>{`PK: ${resource.PK}, SK:${resource.SK}, resourceCode:${resource.Code}`}</code>
      </div>
    );
  };

  if (filteredResources === undefined) {
    return (
      <div className="position-absolute top-50 start-50 translate-middle">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div
      key="resources"
      className="ResourcesIndex placeholder-glow d-flex flex-column flex-sm-row h-100 flex gap-3"
      style={{ maxHeight: "calc(100% - 32px)" }}
    >
      <div className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3">
        <div className="sticky-top d-none d-sm-flex flex-column h-100">
          <div className="d-flex mb-3 pt-3 align-items-center gap-3 flex-shrink-0">
            {subject ? (
              <div className="icon d-block py-2 pe-2">
                <SubjectIcon subject={subject} />
              </div>
            ) : (
              <div className="py-2 pe-2">
                <Cap className="cap" />
              </div>
            )}
            <h3 className="m-0">
              <span className="pe-2">
                {subject ? (
                  subject.title[locale]
                ) : isAuthenticated ? (
                  _("My Resources")
                ) : (
                  <span className="placeholder col-2"></span>
                )}
              </span>
              {/*!!subject?.resourceCount && !!filteredResources?.length && <span className="text-muted">({filteredResources?.length}{filteredResources?.length !== subject?.resourceCount && `/${subject?.resourceCount}`})</span> */}
            </h3>
          </div>
          {/** Show User Resource counts for User Dashboard */}
          {!subject && isAuthenticated && (
            <MyResourcesCount className="mb-3 me-2" />
          )}
          <Filters
            className="pe-2"
            query={query}
            handleQueryItemClick={handleQueryItemClick}
            handleClearClick={handleClearClick}
            resources={resources}
            specItems={specItems}
            noSpecItems={noSpecItems}
            activeFilters={filters}
          />
        </div>

        <div className="d-sm-none">
          {
            <div className="d-flex mb-3 flex-wrap align-items-center">
              <div className="d-flex align-items-center gap-3 d-sm-none">
                {/* subject?.title && <div className="icon d-block py-2 pe-2 "><SubjectIcon subject={subject?.title} /></div> */}
                <h1 className="m-0">
                  {((subject && subject.title[locale]) ?? isAuthenticated) ? (
                    _("My Resources")
                  ) : (
                    <span className="placeholder col-2"></span>
                  )}
                  {/*!!subject?.resourceCount && !!filteredResources?.length && <span className="text-muted">({filteredResources?.length}{filteredResources?.length !== subject?.resourceCount && `/${subject?.resourceCount}`})</span> */}
                </h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn btn-lg btn-light"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <Caret /> {_("Filter")} <Filter />
                </button>
              </div>
            </div>
          }

          {/** Show User Resource counts for User Dashboard */}
          {!subject && isAuthenticated && <MyResourcesCount className="m-0" />}
          <div
            className={`visible offcanvas offcanvas-end${
              showFilter ? " show visible" : ""
            }`}
          >
            <div className="offcanvas-header">
              {/* <h5 className="offcanvas-title" id="offcanvasExampleLabel">{_('Filter')}</h5> */}
              <button
                onClick={() => setShowFilter(false)}
                type="button"
                className="ms-auto btn-close text-reset"
                aria-label="Close"
              ></button>
            </div>
            <div className="offcanvas-body pt-0">
              <Filters
                query={query}
                handleQueryItemClick={handleQueryItemClick}
                handleClearClick={handleClearClick}
                resources={resources}
                specItems={specItems}
                noSpecItems={noSpecItems}
                activeFilters={filters}
              />
            </div>
          </div>
          <div
            onClick={() => setShowFilter(false)}
            className={`offcanvas-backdrop fade${
              showFilter ? " show" : " d-none"
            }`}
          ></div>
        </div>
      </div>
      <div className="col d-flex flex-column h-100 resources-container">
        <ResourceCurrentFilter
          query={query}
          handleClearClick={handleClearClick}
          specItems={specItems}
          handleQueryItemClick={handleQueryItemClick}
        />

        <ResourceSort
          query={query}
          pageKey={subject ? subject.id : "my-resources"}
          updateSearchParams={setSearchParams}
          filteredResources={resources}
        />

        {resources?.length === 0 ? (
          <div className="alert alert-warning">{_("No resources found")}</div>
        ) : (
          <>
            {/* <div className="mb-2 fw-bold fs-5">
            {(filteredResources || []).length > 0 && <div>{(filteredResources || []).length} result(s)</div>}
            {filteredResources?.length === 0 && <div>No results</div>}
          </div> */}

            <div
              className="Resources d-grid justify-content-between h-100"
              role="list"
            >
              {isLoading &&
                [1, 2, 3].map((i) => (
                  <PlaceholderCard key={`placeholder${i}`} />
                ))}

              {filteredResources && filteredResources.length > 0 ? (
                filteredResources.map(
                  (resource, index) => {
                    if (!resource) return null;
                    const acknowledgements = resource.Object?.links?.find(
                      (file: ResourceLink) =>
                        file.label?.trim() === "Acknowledgements",
                    );
                    const imgPath =
                      (resource.Type === "BL"
                        ? `${brand}/${stringToFileName(
                            resource.Subject.id,
                          )}-${resource.Locale.toUpperCase()}/${resource.Code}`
                        : resource.Type +
                          (resource.Type === "QB" && locale === "cy"
                            ? "-cy"
                            : "")) + ".jpg";
                    return (
                      <div
                        key={index}
                        className="pb-4 Resource"
                        role="listitem"
                      >
                        <ErrorBoundary fallback={ErrorFallback(resource)}>
                          <ResourceCard
                            resource={resource as ResourceCardType}
                            acknowledgements={acknowledgements}
                            imgPath={imgPath}
                            user={user}
                          />
                        </ErrorBoundary>
                      </div>
                    );
                  },
                )
              ) : (
                <div className="col-12">
                  <div className="alert alert-warning">
                    {_("no-resources-query")}
                  </div>
                </div>
              )}
            </div>

            <FeatureFlag flag={["paginated-results"]}>
              <Row>
                <div className="d-flex justify-content-center">
                  <Button
                    variant="secondary"
                    disabled={isLoading}
                    onClick={() => {
                      setPage(page + 1);
                      setIsLoading(true);
                    }}
                  >
                    Load More...
                  </Button>
                </div>
              </Row>
            </FeatureFlag>
          </>
        )}
      </div>
    </div>
  );
};

export default ResourcesIndexUI;
